import { useEffect, useMemo, useState } from 'react';
import { useLang } from '../../i18n';

const API = process.env.REACT_APP_API_BASE_URL;

// ISO2 → indicatif téléphonique. Les NOMS de pays sont localisés à l'exécution
// via Intl.DisplayNames (aucun nom codé en dur → traduit dans toutes les langues).
const COUNTRY_DIALS = [
  ['FR', '33'], ['BE', '32'], ['CH', '41'], ['LU', '352'], ['MC', '377'], ['ES', '34'], ['PT', '351'], ['IT', '39'],
  ['DE', '49'], ['NL', '31'], ['GB', '44'], ['IE', '353'], ['AT', '43'], ['PL', '48'], ['RO', '40'], ['GR', '30'],
  ['SE', '46'], ['NO', '47'], ['DK', '45'], ['FI', '358'], ['US', '1'], ['CA', '1'], ['MX', '52'], ['BR', '55'],
  ['AR', '54'], ['CO', '57'], ['PE', '51'], ['VE', '58'], ['EC', '593'], ['CL', '56'], ['MA', '212'], ['DZ', '213'],
  ['TN', '216'], ['SN', '221'], ['CI', '225'], ['CM', '237'], ['CD', '243'], ['GH', '233'], ['NG', '234'], ['KE', '254'],
  ['ZA', '27'],
];

// Compact "one screen" : pas de labels (placeholders), champs 44px, gaps serrés.
const css = `
  .cf { margin-top: 12px; max-width: 100%; }
  .cf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .cf-box { display: flex; align-items: center; gap: 8px; min-width: 0; height: 44px; background: #100e26; border-radius: 10px;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); padding: 0 12px; transition: box-shadow .15s; }
  .cf-box.span2 { grid-column: span 2; }
  .cf-box.focus { box-shadow: inset 0 0 0 1.5px #76e1ff; }
  .cf-box.bad { box-shadow: inset 0 0 0 1.5px #ff5a6a; }
  .cf-box input { flex: 1 1 auto; width: 100%; min-width: 0; background: none; border: none; outline: none; color: #fff; font-size: 14px; font-family: inherit; }
  .cf-box input::placeholder { color: #5a5972; }
  .cf-dialwrap { position: relative; flex: 0 0 auto; }
  .cf-dial { display: flex; align-items: center; gap: 6px; height: 30px; padding: 0 9px; border: none; border-radius: 8px;
    background: #211f42; color: #fff; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
  .cf-dial .chev { color: #9b9ab0; transition: transform .15s; }
  .cf-dial.open .chev { transform: rotate(180deg); }
  .cf-flag { width: 20px; height: 14px; border-radius: 2px; object-fit: cover; display: block; box-shadow: 0 0 0 1px rgba(255,255,255,.14); }
  .cf-ddl { position: absolute; top: 36px; left: 0; z-index: 50; width: 232px; max-height: 244px; overflow-y: auto;
    background: #1d1c3c; border-radius: 10px; padding: 5px; box-shadow: 0 12px 34px rgba(0,0,0,.55), inset 0 0 0 1px rgba(255,255,255,.1); }
  .cf-ddl-item { display: flex; align-items: center; gap: 9px; width: 100%; height: 36px; padding: 0 9px; border: none;
    border-radius: 7px; background: none; color: #e8e8f0; font-family: inherit; font-size: 13px; cursor: pointer; text-align: left; }
  .cf-ddl-item:hover { background: #2a2850; }
  .cf-ddl-item.on { background: rgba(118,225,255,.12); }
  .cf-ddl-item .nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cf-ddl-item .dl { color: #9b9ab0; font-weight: 600; }
  .cf-mark { flex: none; display: inline-flex; align-items: center; height: 20px; }
  .cf-visa { font-style: italic; font-weight: 800; font-size: 15px; letter-spacing: .5px; color: #fff; line-height: 1; }
  .cf-amex { font-weight: 800; font-size: 10px; letter-spacing: .4px; color: #fff; background: #2671b8; padding: 3px 4px; border-radius: 3px; line-height: 1; }
  .cf-mc { display: inline-flex; }
  .cf-mc i { width: 16px; height: 16px; border-radius: 50%; display: block; }
  .cf-mc .r { background: #eb001b; } .cf-mc .y { background: #f79e1b; margin-left: -7px; mix-blend-mode: screen; }
  .cf-mark-all { gap: 7px; opacity: .4; }
  .cf-mark-all .cf-visa { font-size: 13px; }
  .cf-mark-all .cf-amex { font-size: 9px; padding: 2px 3px; }
  .cf-mark-all .cf-mc i { width: 13px; height: 13px; }
  .cf-mark-all .cf-mc .y { margin-left: -6px; }
  .cf-lock { color: #4a4963; flex: none; }
  .cf-err { grid-column: span 2; font-size: 11px; color: #ff6b7a; margin: -2px 0 0 2px; }
  .cf-apierr { background: rgba(255,90,106,.1); color: #ff8b97; box-shadow: inset 0 0 0 1px rgba(255,90,106,.3);
    border-radius: 10px; padding: 9px 12px; font-size: 12.5px; margin-top: 10px; }
  .cf-sum { margin-top: 12px; padding-top: 10px; box-shadow: inset 0 1px 0 rgba(255,255,255,.07); }
  .cf-srow { display: flex; align-items: center; justify-content: space-between; padding: 3px 2px; }
  .cf-srow .k { color: #9b9ab0; font-size: 12px; }
  .cf-srow .v { color: #fff; font-size: 13px; }
  .cf-srow.total .k { font-weight: 800; color: #fff; font-size: 13px; }
  .cf-srow.total .v { font-weight: 800; font-size: 15px; color: #76e1ff; }
  .cf-pay { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 46px; margin-top: 12px;
    border: none; border-radius: 12px; cursor: pointer; background: linear-gradient(90deg, #8187f6, #76e1ff);
    color: #fff; font-size: 15px; font-weight: 800; font-family: inherit; }
  .cf-pay:disabled { background: #1d1c3c; color: #5a5972; cursor: not-allowed; }
  .cf-pay:disabled .cf-spin circle { stroke: #76e1ff; }
  .cf-spin { animation: cf-rot .7s linear infinite; }
  @keyframes cf-rot { to { transform: rotate(360deg); } }
  .cf-secure { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 9px; font-size: 11px; color: #9b9ab0; }
  .cf-help { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 10px; padding: 9px 12px;
    border-radius: 10px; background: rgba(118,225,255,.07); box-shadow: inset 0 0 0 1px rgba(118,225,255,.16);
    font-size: 11.5px; line-height: 1.35; color: #9fb9c4; text-align: center; text-decoration: none; }
  .cf-help:hover { background: rgba(118,225,255,.12); }
  .cf-help svg { flex: none; color: #76e1ff; }
`;

function luhn(num) {
  let sum = 0, alt = false;
  for (let i = num.length - 1; i >= 0; i--) {
    let d = +num[i];
    if (alt) { d *= 2; if (d > 9) d -= 9; }
    sum += d; alt = !alt;
  }
  return num.length > 0 && sum % 10 === 0;
}
function brandOf(num) {
  if (/^4/.test(num)) return ['Visa', 16];
  if (/^(5[1-5]|2[2-7])/.test(num)) return ['Mastercard', 16];
  if (/^3[47]/.test(num)) return ['Amex', 15];
  return ['', 16];
}
const digits = (s) => (s || '').replace(/\D/g, '');
// Vrai drapeau (image) — les emojis drapeaux ne s'affichent pas sous Windows.
const flagUrl = (iso) => 'https://flagcdn.com/w40/' + String(iso).toLowerCase() + '.png';

// Logos BIN affichés à droite du numéro : TOUS estompés par défaut, puis
// uniquement la marque détectée. Défini au niveau module (pas de remount).
const VISA_MARK = <span className="cf-visa">VISA</span>;
const MC_MARK   = <span className="cf-mc"><i className="r" /><i className="y" /></span>;
const AMEX_MARK = <span className="cf-amex">AMEX</span>;
function BrandMark({ brand }) {
  if (brand === 'Visa')       return <span className="cf-mark">{VISA_MARK}</span>;
  if (brand === 'Mastercard') return <span className="cf-mark">{MC_MARK}</span>;
  if (brand === 'Amex')       return <span className="cf-mark">{AMEX_MARK}</span>;
  return <span className="cf-mark cf-mark-all">{VISA_MARK}{MC_MARK}{AMEX_MARK}</span>;
}

/**
 * Formulaire carte INLINE compact (Payou MoneyEUR_BP), rendu dans l'onglet
 * « Carte » de la page de dépôt (pas d'overlay, pensé "one screen"). Collecte
 * carte + payeur, poste vers api/payments/payou-card.php (appel API Payou côté
 * serveur) puis redirige vers le payUrl 3DS. IP + pays jamais envoyés (serveur).
 *
 * Props : summ (EUR string), currency, paysys, prefix, detectedCC (ISO2),
 * payLabel (bouton), amountLabel + creditedLabel (résumé), amountValid, minMessage.
 *
 * onBeforeSubmit (optionnel, async) : exécuté AVANT l'appel carte. Doit renvoyer
 *   { ok: true } pour continuer, ou { ok: false, message } pour abandonner (ex. :
 *   upload KYC/JDD dans le funnel de retrait → on paie seulement si l'upload passe).
 * summary (optionnel) : lignes du récap [{ k, v, total? }]. Par défaut on affiche
 *   « Montant » + « Total crédité » (dépôt). Le funnel passe une ligne unique
 *   (ex. « Frais KYC ») car « Total crédité » n'a pas de sens pour des frais.
 */
export default function CardForm({ summ, currency, paysys, prefix = '', detectedCC, payLabel, amountLabel, creditedLabel, amountValid, minMessage, onBeforeSubmit, summary }) {
  const { t, lang } = useLang();

  // Noms de pays localisés (Intl.DisplayNames), triés dans la langue courante.
  const countries = useMemo(() => {
    let dn = null;
    try { dn = new Intl.DisplayNames([lang], { type: 'region' }); } catch { /* vieux moteur */ }
    return COUNTRY_DIALS
      .map(([iso, d]) => [iso, d, (dn && dn.of(iso)) || iso])
      .sort((a, b) => a[2].localeCompare(b[2], lang));
  }, [lang]);

  const [countryIso, setCountryIso] = useState(() => (
    COUNTRY_DIALS.some((c) => c[0] === (detectedCC || 'FR')) ? (detectedCC || 'FR') : 'FR'
  ));
  const [dialOpen, setDialOpen] = useState(false);
  const dial = countries.find((c) => c[0] === countryIso)?.[1] || '33';
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');

  const [focusKey, setFocusKey] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Facturation déjà enregistrée en BDD (tel/adresse/ville/CP) : collectée au 1er
  // paiement puis sauvegardée côté serveur (payou-card.php). Si elle est complète,
  // on masque ces champs et on renvoie les valeurs stockées → form raccourci.
  const [savedBilling, setSavedBilling] = useState(null);
  const billingLocked = !!savedBilling;
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/user/profile.php`);
        const d = await res.json();
        const b = d?.data;
        if (alive && d?.success && b && b.telephone && b.adresse && b.ville && b.code_postal) {
          setSavedBilling({ phone: String(b.telephone), address: String(b.adresse), city: String(b.ville), postalCode: String(b.code_postal) });
        }
      } catch { /* profil indispo → form complet (fallback) */ }
    })();
    return () => { alive = false; };
  }, []);

  const brand = useMemo(() => brandOf(digits(cardNumber))[0], [cardNumber]);
  const cvvLen = brand === 'Amex' ? 4 : 3;

  // Prénom / nom dérivés du titulaire (ex. "JEAN DUPONT" → prénom "JEAN", nom
  // "DUPONT"). Payou exige firstName + lastName ; on ne montre qu'un seul champ.
  const holderParts = cardHolder.trim().split(/\s+/).filter(Boolean);
  const firstName = holderParts[0] || '';
  const lastName = holderParts.slice(1).join(' ');

  const validate = () => {
    const e = {};
    if (!billingLocked) {
      if (digits(phone).length < 6) e.phone = ' ';
      if (!address.trim()) e.address = ' ';
      if (!city.trim()) e.city = ' ';
      if (postalCode.trim().length < 3) e.postalCode = ' ';
    }
    if (cardHolder.trim().split(/\s+/).filter(Boolean).length < 2) e.cardHolder = t('deposit.checkout.errName');

    const d = digits(cardNumber);
    const [b, maxLen] = brandOf(d);
    if (d.length < (b === 'Amex' ? 15 : 13)) e.cardNumber = t('deposit.checkout.errCardIncomplete');
    else if (d.length !== maxLen || !luhn(d)) e.cardNumber = t('deposit.checkout.errCardInvalid');

    const m = expiry.match(/^(\d{2})\/(\d{2})$/);
    if (!m) e.expiry = ' ';
    else {
      const mm = +m[1], yy = +m[2];
      const now = new Date(), curY = now.getFullYear() % 100, curM = now.getMonth() + 1;
      if (mm < 1 || mm > 12) e.expiry = t('deposit.checkout.errExpiryMonth');
      else if (yy < curY || (yy === curY && mm < curM)) e.expiry = t('deposit.checkout.errExpired');
    }

    if (digits(cvv).length !== cvvLen) e.cvv = ' ';
    return e;
  };

  const errors = validate();
  const isValid = Object.keys(errors).length === 0;
  const errOf = (k) => (touched ? errors[k] : '');
  // N'affiche un texte d'erreur que s'il est "parlant" (≠ simple espace sentinelle).
  const msgOf = (k) => { const v = errOf(k); return v && v !== ' ' ? v : ''; };

  useEffect(() => { setApiError(''); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phone, address, city, postalCode, cardHolder, cardNumber, expiry, cvv]);

  // Fermeture du sélecteur pays au clic extérieur / Échap.
  useEffect(() => {
    if (!dialOpen) return;
    const onDown = (e) => { if (!e.target.closest('.cf-dialwrap')) setDialOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setDialOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [dialOpen]);

  const onCardInput = (v) => {
    let d = digits(v);
    const [b, maxLen] = brandOf(d);
    d = d.slice(0, maxLen);
    const grouped = b === 'Amex'
      ? d.replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, '$1 $2 $3').trim()
      : d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    setCardNumber(grouped);
  };
  const onExpiryInput = (v) => {
    let d = digits(v).slice(0, 4);
    if (d.length >= 3) d = d.slice(0, 2) + '/' + d.slice(2);
    setExpiry(d);
  };

  const submit = async () => {
    if (submitting) return; // anti double-clic (le bouton est aussi disabled)
    setTouched(true);
    if (!amountValid) { setApiError(minMessage || ''); return; }
    if (!isValid) return;

    setApiError('');
    setSubmitting(true);

    // Hook pré-soumission (ex. upload KYC/JDD) : on ne paie que s'il réussit.
    if (onBeforeSubmit) {
      try {
        const pre = await onBeforeSubmit();
        if (!pre || pre.ok === false) { setApiError(pre?.message || t('deposit.checkout.errApi')); setSubmitting(false); return; }
      } catch {
        setApiError(t('deposit.checkout.errApi')); setSubmitting(false); return;
      }
    }

    // Facturation : valeurs enregistrées si dispo (champs masqués), sinon saisie.
    const phoneNumber = billingLocked ? savedBilling.phone : '+' + dial + digits(phone).replace(/^0+/, '');
    const billAddress = billingLocked ? savedBilling.address : address.trim();
    const billCity = billingLocked ? savedBilling.city : city.trim();
    const billPostal = billingLocked ? savedBilling.postalCode : postalCode.trim();
    const m = expiry.match(/^(\d{2})\/(\d{2})$/);

    try {
      const res = await fetch(`${API}/api/payments/payou-card.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summ, paysys, currency, prefix,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber,
          address: billAddress,
          city: billCity,
          state: '',
          postalCode: billPostal,
          cardHolder: cardHolder.trim(),
          cardNumber: digits(cardNumber),
          cardExpirationMonth: m[1],
          cardExpirationYear: '20' + m[2],
          cardCvv: digits(cvv),
        }),
      });
      const d = await res.json();
      if (d && d.success && d.payUrl) {
        window.location.href = d.payUrl; // reste en "submitting" pendant la redirection
        return;
      }
      setApiError(d?.message || t('deposit.checkout.errApi'));
      setSubmitting(false);
    } catch {
      setApiError(t('deposit.checkout.errApi'));
      setSubmitting(false);
    }
  };

  const boxCls = (k, span) => 'cf-box' + (span ? ' span2' : '') + (focusKey === k ? ' focus' : '') + (errOf(k) ? ' bad' : '');
  const bind = (k) => ({ onFocus: () => setFocusKey(k), onBlur: () => setFocusKey('') });

  return (
    <div className="cf">
      <style>{css}</style>

      <div className="cf-grid">
        {!billingLocked && (<>
        <div className={boxCls('phone', true)}>
          <div className="cf-dialwrap">
            <button type="button" className={'cf-dial' + (dialOpen ? ' open' : '')} onClick={() => setDialOpen((o) => !o)} aria-label="Indicatif pays">
              <img className="cf-flag" src={flagUrl(countryIso)} alt="" loading="lazy" />
              <span>+{dial}</span>
              <svg className="chev" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5 5 6.5 8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            {dialOpen && (
              <div className="cf-ddl">
                {countries.map(([iso, d, name]) => (
                  <button type="button" key={iso + d} className={'cf-ddl-item' + (iso === countryIso ? ' on' : '')} onClick={() => { setCountryIso(iso); setDialOpen(false); }}>
                    <img className="cf-flag" src={flagUrl(iso)} alt="" loading="lazy" />
                    <span className="nm">{name}</span>
                    <span className="dl">+{d}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input type="tel" inputMode="tel" placeholder={t('deposit.checkout.phone')} aria-label={t('deposit.checkout.phone')} value={phone} {...bind('phone')} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div className={boxCls('address', true)}>
          <input placeholder={t('deposit.checkout.address')} aria-label={t('deposit.checkout.address')} value={address} autoComplete="street-address" {...bind('address')} onChange={(e) => setAddress(e.target.value)} />
        </div>

        <div className={boxCls('city')}>
          <input placeholder={t('deposit.checkout.city')} aria-label={t('deposit.checkout.city')} value={city} autoComplete="address-level2" {...bind('city')} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className={boxCls('postalCode')}>
          <input placeholder={t('deposit.checkout.postalCode')} aria-label={t('deposit.checkout.postalCode')} value={postalCode} autoComplete="postal-code" {...bind('postalCode')} onChange={(e) => setPostalCode(e.target.value)} />
        </div>
        </>)}

        <div className={boxCls('cardHolder', true)}>
          <input placeholder={t('deposit.checkout.cardHolder')} aria-label={t('deposit.checkout.cardHolder')} value={cardHolder} autoComplete="cc-name" {...bind('cardHolder')} onChange={(e) => setCardHolder(e.target.value.toUpperCase().replace(/[^A-ZÀ-Ü '-]/g, ''))} />
        </div>
        {msgOf('cardHolder') && <div className="cf-err">{msgOf('cardHolder')}</div>}

        <div className={boxCls('cardNumber', true)}>
          <input inputMode="numeric" autoComplete="cc-number" placeholder={t('deposit.checkout.cardNumber')} aria-label={t('deposit.checkout.cardNumber')} value={cardNumber} {...bind('cardNumber')} onChange={(e) => onCardInput(e.target.value)} />
          <BrandMark brand={brand} />
        </div>
        {msgOf('cardNumber') && <div className="cf-err">{msgOf('cardNumber')}</div>}

        <div className={boxCls('expiry')}>
          <input inputMode="numeric" autoComplete="cc-exp" placeholder={t('deposit.checkout.expiryPh')} aria-label={t('deposit.checkout.expiry')} maxLength={5} value={expiry} {...bind('expiry')} onChange={(e) => onExpiryInput(e.target.value)} />
        </div>
        <div className={boxCls('cvv')}>
          <input inputMode="numeric" autoComplete="cc-csc" placeholder="CVV" aria-label={t('deposit.checkout.cvv')} maxLength={4} value={cvv} {...bind('cvv')} onChange={(e) => setCvv(digits(e.target.value).slice(0, 4))} />
          <svg className="cf-lock" width="13" height="13" viewBox="0 0 14 15" fill="none"><rect x="2.5" y="6.5" width="9" height="6.5" rx="1.6" stroke="currentColor" strokeWidth="1.3" /><path d="M4.5 6.5V4.7a2.5 2.5 0 015 0v1.8" stroke="currentColor" strokeWidth="1.3" /></svg>
        </div>
        {(msgOf('expiry') || msgOf('cvv')) && <div className="cf-err">{msgOf('expiry') || msgOf('cvv')}</div>}
      </div>

      {apiError && <div className="cf-apierr">{apiError}</div>}

      <div className="cf-sum">
        {summary ? (
          summary.map((r, i) => (
            <div key={i} className={'cf-srow' + (r.total ? ' total' : '')}><span className="k">{r.k}</span><span className="v">{r.v}</span></div>
          ))
        ) : (
          <>
            <div className="cf-srow"><span className="k">{t('deposit.amount')}</span><span className="v">{amountLabel}</span></div>
            <div className="cf-srow total"><span className="k">{t('deposit.totalCredited')}</span><span className="v">{creditedLabel}</span></div>
          </>
        )}
      </div>

      <button className="cf-pay" onClick={submit} disabled={submitting}>
        {submitting
          ? (<>
              <svg className="cf-spin" width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.8" /><path d="M8 1.8a6.2 6.2 0 016.2 6.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              {t('deposit.checkout.processing')}
            </>)
          : (<>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="3.5" width="13" height="9" rx="2" stroke="currentColor" strokeWidth="1.4" /><path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.4" /></svg>
              {t('deposit.checkout.pay', { amount: payLabel })}
            </>)}
      </button>

      <div className="cf-secure">
        <svg width="12" height="13" viewBox="0 0 14 15" fill="none"><rect x="2.5" y="6.5" width="9" height="6.5" rx="1.6" stroke="currentColor" strokeWidth="1.3" /><path d="M4.5 6.5V4.7a2.5 2.5 0 015 0v1.8" stroke="currentColor" strokeWidth="1.3" /></svg>
        {t('deposit.checkout.secured')}
      </div>

      <a className="cf-help" href="https://t.me/officialsupport_bet" target="_blank" rel="noopener noreferrer">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M21.9 4.3 18.6 19.4c-.2 1.1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-5 9.1-8.2c.4-.3-.1-.5-.6-.2L6 13.1l-4.8-1.5c-1-.3-1.1-1 .2-1.5l18.8-7.2c.9-.3 1.6.2 1.3 1.4Z" /></svg>
        {t('deposit.checkout.help')}
      </a>
    </div>
  );
}
